import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  AlertTriangle, 
  Database, 
  Shield, 
  Code, 
  Zap,
  Terminal,
  HardDrive,
  Wifi,
  Clock,
  X,
  Check,
  RefreshCw
} from 'lucide-react';
import axios from '../lib/axios';
import { useUserStore } from '../stores/useUserStore';
import toast from 'react-hot-toast';

const DeveloperNotificationPanel = () => {
  const { user } = useUserStore();
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(false);

  // טעינת התראות מפתח
  const fetchDeveloperNotifications = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/notifications?unreadOnly=true&limit=20');
      const devNotifications = response.data.notifications.filter(n => 
        n.type === 'security' || 
        (n.type === 'system' && (n.priority === 'urgent' || n.priority === 'high'))
      );
      setNotifications(devNotifications);
      
      // סטטיסטיקות מהירות
      const statsResponse = await axios.get('/notifications/admin/stats');
      setStats(statsResponse.data);
    } catch (error) {
      console.error('Error fetching developer notifications:', error);
      toast.error('שגיאה בטעינת התראות מפתח');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchDeveloperNotifications();
    }
  }, [isOpen]);

  // אייקון לפי סוג שגיאה
  const getErrorIcon = (notification) => {
    const title = notification.title.toLowerCase();
    
    if (title.includes('timeout') || title.includes('api')) return <Clock className="w-5 h-5 text-red-500" />;
    if (title.includes('mongodb') || title.includes('database')) return <Database className="w-5 h-5 text-orange-500" />;
    if (title.includes('security') || title.includes('פריצה')) return <Shield className="w-5 h-5 text-red-600" />;
    if (title.includes('memory') || title.includes('cpu')) return <HardDrive className="w-5 h-5 text-yellow-500" />;
    if (title.includes('code') || title.includes('עדכון')) return <Code className="w-5 h-5 text-blue-500" />;
    if (title.includes('network') || title.includes('proxy')) return <Wifi className="w-5 h-5 text-purple-500" />;
    if (title.includes('pwa') || title.includes('service')) return <Zap className="w-5 h-5 text-green-500" />;
    return <AlertTriangle className="w-5 h-5 text-red-500" />;
  };

  // צבע לפי עדיפות
  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'urgent': return 'border-red-500 bg-red-500/10';
      case 'high': return 'border-orange-500 bg-orange-500/10';
      case 'medium': return 'border-yellow-500 bg-yellow-500/10';
      default: return 'border-gray-500 bg-gray-500/10';
    }
  };

  // הצגת זמן יחסי
  const getTimeAgo = (date) => {
    const now = new Date();
    const notifDate = new Date(date);
    const diffInMinutes = Math.floor((now - notifDate) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'עכשיו';
    if (diffInMinutes < 60) return `לפני ${diffInMinutes} דקות`;
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `לפני ${diffInHours} שעות`;
    const diffInDays = Math.floor(diffInHours / 24);
    return `לפני ${diffInDays} ימים`;
  };

  // רק למנהלים
  if (!user || user.role !== 'admin') {
    return null;
  }

  return (
    <>
      {/* כפתור הפעלת הפאנל */}
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-4 left-4 z-40 bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-700 hover:to-orange-700 text-white p-3 rounded-full shadow-lg transition-all duration-300 transform hover:scale-110"
        title="פאנל התראות מפתח"
      >
        <div className="relative">
          <Terminal className="w-6 h-6" />
          {notifications.length > 0 && (
            <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center animate-pulse">
              {notifications.length > 9 ? '9+' : notifications.length}
            </span>
          )}
        </div>
      </button>

      {/* פאנל התראות */}
      <AnimatePresence>
        {isOpen && (
          <>
            {/* רקע מטושטש */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50"
              onClick={() => setIsOpen(false)}
            />

            {/* הפאנל עצמו */}
            <motion.div
              initial={{ x: -400, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: -400, opacity: 0 }}
              transition={{ type: "spring", damping: 25, stiffness: 200 }}
              className="fixed left-0 top-0 h-full w-96 bg-gradient-to-b from-gray-900 via-gray-800 to-gray-900 border-r border-gray-700 z-50 overflow-hidden"
            >
              {/* כותרת */}
              <div className="p-4 border-b border-gray-700 bg-gradient-to-r from-red-900/20 to-orange-900/20">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Terminal className="w-6 h-6 text-red-400" />
                    <h2 className="text-lg font-bold text-white">Developer Console</h2>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={fetchDeveloperNotifications}
                      disabled={loading}
                      className="p-1 hover:bg-gray-700 rounded transition-colors"
                    >
                      <RefreshCw className={`w-4 h-4 text-gray-400 ${loading ? 'animate-spin' : ''}`} />
                    </button>
                    <button
                      onClick={() => setIsOpen(false)}
                      className="p-1 hover:bg-gray-700 rounded transition-colors"
                    >
                      <X className="w-4 h-4 text-gray-400" />
                    </button>
                  </div>
                </div>

                {/* סטטיסטיקות מהירות */}
                <div className="mt-3 grid grid-cols-3 gap-2 text-xs">
                  <div className="bg-red-500/20 border border-red-500/30 rounded p-2 text-center">
                    <div className="text-red-400 font-bold">{stats.totalUnread || 0}</div>
                    <div className="text-gray-400">לא נקראו</div>
                  </div>
                  <div className="bg-orange-500/20 border border-orange-500/30 rounded p-2 text-center">
                    <div className="text-orange-400 font-bold">{notifications.filter(n => n.priority === 'urgent').length}</div>
                    <div className="text-gray-400">דחופות</div>
                  </div>
                  <div className="bg-yellow-500/20 border border-yellow-500/30 rounded p-2 text-center">
                    <div className="text-yellow-400 font-bold">{notifications.filter(n => n.type === 'security').length}</div>
                    <div className="text-gray-400">אבטחה</div>
                  </div>
                </div>
              </div>

              {/* רשימת התראות */}
              <div className="flex-1 overflow-y-auto p-4 space-y-3 max-h-full">
                {loading ? (
                  <div className="flex items-center justify-center py-8">
                    <RefreshCw className="w-6 h-6 text-gray-400 animate-spin" />
                    <span className="ml-2 text-gray-400">טוען התראות...</span>
                  </div>
                ) : notifications.length === 0 ? (
                  <div className="text-center py-8">
                    <Check className="w-12 h-12 text-green-500 mx-auto mb-3" />
                    <p className="text-gray-400">כל המערכות פועלות תקין! 🎉</p>
                  </div>
                ) : (
                  notifications.map((notification, index) => (
                    <motion.div
                      key={notification._id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1 }}
                      className={`border-l-4 rounded-lg p-3 ${getPriorityColor(notification.priority)} hover:bg-gray-800/50 transition-colors cursor-pointer group`}
                      onClick={() => {
                        if (notification.actionUrl) {
                          window.location.href = notification.actionUrl;
                        }
                      }}
                    >
                      <div className="flex items-start gap-3">
                        {getErrorIcon(notification)}
                        <div className="flex-1 min-w-0">
                          <h3 className="font-medium text-white text-sm mb-1 leading-tight">
                            {notification.title}
                          </h3>
                          <p className="text-gray-400 text-xs mb-2 leading-relaxed">
                            {notification.message}
                          </p>
                          
                          {/* מטא-דאטה טכנית */}
                          {notification.data && (
                            <div className="text-xs text-gray-500 space-y-1">
                              {notification.data.errorCount && (
                                <div>🔢 שגיאות: {notification.data.errorCount}</div>
                              )}
                              {notification.data.memoryUsage && (
                                <div>💾 זיכרון: {notification.data.memoryUsage}%</div>
                              )}
                              {notification.data.requestsPerHour && (
                                <div>📊 בקשות/שעה: {notification.data.requestsPerHour.toLocaleString()}</div>
                              )}
                              {notification.data.version && (
                                <div>🔖 גרסה: {notification.data.version}</div>
                              )}
                            </div>
                          )}
                          
                          <div className="flex items-center justify-between mt-2">
                            <span className="text-xs text-gray-500">
                              {getTimeAgo(notification.createdAt)}
                            </span>
                            <span className={`text-xs px-2 py-1 rounded ${
                              notification.priority === 'urgent' ? 'bg-red-500/20 text-red-400' :
                              notification.priority === 'high' ? 'bg-orange-500/20 text-orange-400' :
                              'bg-yellow-500/20 text-yellow-400'
                            }`}>
                              {notification.priority.toUpperCase()}
                            </span>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  ))
                )}
              </div>

              {/* פוטר עם פעולות */}
              <div className="p-4 border-t border-gray-700 bg-gray-900/50">
                <button
                  onClick={() => window.location.href = '/admin'}
                  className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white py-2 px-4 rounded-lg transition-all duration-300 text-sm font-medium"
                >
                  🔧 פתח Admin Console
                </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default DeveloperNotificationPanel; 